import { MAX_EVENTS_PER_BATCH, type StorefrontEvent, type StorefrontEventName } from '@xshop/shared';
import { currentSession } from './session';
import { adsAllowed, firePixelEvent, funnelEventId, startPixels, type PixelEventDetail } from './pixels';
import { fireAppEvent, startApps } from './apps';
import { adVisitorId } from './pixels/adVisitorId';

const ENDPOINT = '/api/v1/storefront-events';
const FLUSH_AT = 10;
const FLUSH_INTERVAL_MS = 15_000;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Everything an event may carry beyond its name and timestamp, which the tracker fills in. The
// commerce fields are for the ad pixels only; the analytics beacon schema ignores them.
export type StorefrontEventDetail = Omit<StorefrontEvent, 'name' | 'ts' | 'eventId'>
  & Pick<PixelEventDetail, 'numItems' | 'orderNumber'>;

const queue: StorefrontEvent[] = [];
let started = false;
let timer: ReturnType<typeof setInterval> | null = null;

// Reports whether the visitor asked not to be tracked through Do Not Track or Global Privacy Control.
function optedOut(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string };
  const dnt = nav.doNotTrack ?? nav.msDoNotTrack ?? (window as { doNotTrack?: string }).doNotTrack;
  return dnt === '1' || dnt === 'yes' || nav.globalPrivacyControl === true;
}

// Drops an id the server would reject, so one bad value cannot fail the whole batch.
function validId(value: string | undefined): string | undefined {
  return value && UUID_RE.test(value) ? value : undefined;
}

// Reads the campaign tags off the current URL so first-touch attribution survives the visit.
function campaignDetail(): StorefrontEventDetail {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source')?.slice(0, 100) || undefined,
    utmMedium: params.get('utm_medium')?.slice(0, 100) || undefined,
    utmCampaign: params.get('utm_campaign')?.slice(0, 100) || undefined
  };
}

// Posts a batch with sendBeacon, falling back to a keepalive fetch where beacons are unavailable.
function send(body: string): void {
  const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
  if (navigator.sendBeacon?.(ENDPOINT, blob)) return;
  void fetch(ENDPOINT, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain' } })
    .catch(() => undefined);
}

// Ships everything queued and empties the queue; a failure loses metrics, never a page.
function flush(): void {
  if (queue.length === 0) return;
  try {
    const session = currentSession();
    const events = queue.splice(0, MAX_EVENTS_PER_BATCH);
    const visitorId = adsAllowed() ? adVisitorId() : undefined;
    send(JSON.stringify({ sessionKey: session.key, sessionStartedAt: session.startedAt, visitorId, events }));
  } catch {
    queue.length = 0;
  }
}

// Queues one storefront event and fires the same step at the configured ad pixels; never throws,
// never blocks rendering, silent when opted out.
export function trackEvent(name: StorefrontEventName, detail: StorefrontEventDetail = {}): void {
  try {
    if (!started || optedOut()) return;
    const eventId = funnelEventId(name, detail);
    const { numItems: _count, orderNumber: _number, ...beacon } = detail;
    queue.push({
      ...beacon,
      path: (detail.path ?? window.location.pathname).slice(0, 512),
      productId: validId(detail.productId),
      orderId: validId(detail.orderId),
      eventId,
      name,
      ts: Date.now()
    });
    if (eventId) firePixelEvent(name, detail, eventId);
    fireAppEvent(name, eventId ? { ...detail, eventId } : detail);
    if (queue.length >= FLUSH_AT) flush();
  } catch {
    // Tracking is never allowed to surface an error into the store.
  }
}

// Reports whether this visit is tracked at all: started, and not opted out through DNT or GPC.
export const trackingAllowed = (): boolean => started && !optedOut();

// Records the landing page with its referrer and campaign tags, the session's first-touch row.
export function trackPageView(): void {
  trackEvent('page_view', {
    ...campaignDetail(),
    path: window.location.pathname.slice(0, 512),
    referrer: document.referrer ? document.referrer.slice(0, 1024) : undefined
  });
}

// Wires the page-hide and interval flushes exactly once, loads the store's ad pixels, then
// records the landing page view. Opting out through DNT or GPC skips the pixels too.
export function startTracking(): void {
  try {
    if (started || typeof window === 'undefined' || optedOut()) return;
    started = true;
    addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
    addEventListener('pagehide', flush);
    timer = setInterval(flush, FLUSH_INTERVAL_MS);
    void startPixels();
    void startApps();
    trackPageView();
  } catch {
    started = false;
  }
}

// Stops the interval flush after a final send; used when the storefront unmounts in development.
export function stopTracking(): void {
  flush();
  if (timer) clearInterval(timer);
  timer = null;
}
